/**
 * Updates the recently viewed products in localStorage.
 * Also records first-party browse affinity (pcl_browse) for the homepage marketing band.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {string} First-party browse-affinity key (not sent to GA/FB) */
  static #BROWSE_KEY = 'pcl_browse';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;
  /** @static @constant {number} Max browse-affinity products */
  static #MAX_BROWSE = 8;

  static #HIDE_BUCKETS = ['pokemon', 'mtg'];

  static #ARTIST_VENDORS = [
    'shiku designs',
    'shiku',
    'dainen creative',
    'dainen',
    'georges glorious goods',
    "george's glorious goods",
    'luck of kings',
    'the murmuring mystic',
    'murmuring mystic',
    'arcane engravings',
    'ashalind draws',
    'ashalind',
    'dmitriy logunkov',
    'firemistvalley',
    'jacinta t artistry',
    'jacinta',
    'resinated',
    'uwu mart',
    'beeflover1_',
    'beeflover',
  ];

  static #ARTIST_HANDLES = [
    'artists-collection',
    'artist-new',
    'artist-prints',
    'artist-keychains',
    'artist-stickers',
    'artist-washi',
    'shiku-designs',
    'dainen-creative',
    'georges-glorious-goods',
    'luck-of-kings',
    'the-murmuring-mystic',
    'arcane-engravings',
    'dmitriy-logunkov',
    'uwu-mart',
    'artist-ashalind-draws',
    'artist-firemistvalley',
    'artist-resinated',
    'jacinta-t-artistry',
    'beeflover1_-local-artist',
  ];

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   * @param {object} [meta] - Optional PDP metadata for browse affinity.
   */
  static addProduct(productId, meta) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));

    if (meta && typeof meta === 'object') {
      this.trackBrowse(meta);
    }
  }

  /**
   * Records a product view into pcl_browse and recomputes bucket scores.
   * @param {object} meta
   */
  static trackBrowse(meta) {
    const state = this.getBrowse();
    const entry = {
      id: String(meta.id ?? ''),
      type: String(meta.type ?? ''),
      tags: Array.isArray(meta.tags) ? meta.tags.map(String) : [],
      vendor: String(meta.vendor ?? ''),
      handle: String(meta.handle ?? ''),
      title: String(meta.title ?? ''),
      collections: Array.isArray(meta.collections) ? meta.collections.map(String) : [],
      ts: Date.now(),
    };

    if (!entry.id) return;

    state.products = (state.products || []).filter((p) => String(p.id) !== entry.id);
    state.products.unshift(entry);
    state.products = state.products.slice(0, this.#MAX_BROWSE);

    const scored = this.scoreBuckets(state.products);
    state.buckets = scored.buckets;
    state.lastBucket = scored.lastBucket;

    localStorage.setItem(this.#BROWSE_KEY, JSON.stringify(state));
  }

  /**
   * Recency-weighted bucket scores. Last view ×3, previous ×2, older ×1.
   * Winner = highest score; tie → most recent.
   * @param {Array<object>} products
   */
  static scoreBuckets(products) {
    /** @type {Record<string, number>} */
    const buckets = {};
    /** @type {Record<string, number>} */
    const firstSeen = {};

    (products || []).forEach((product, index) => {
      const bucket = this.classifyProduct(product);
      if (!bucket) return;
      const weight = index === 0 ? 3 : index === 1 ? 2 : 1;
      buckets[bucket] = (buckets[bucket] || 0) + weight;
      if (firstSeen[bucket] === undefined) firstSeen[bucket] = index;
    });

    let lastBucket = '';
    let bestScore = -1;
    let bestRecency = Number.POSITIVE_INFINITY;

    Object.keys(buckets).forEach((name) => {
      const score = buckets[name];
      const recency = firstSeen[name];
      if (score > bestScore || (score === bestScore && recency < bestRecency)) {
        lastBucket = name;
        bestScore = score;
        bestRecency = recency;
      }
    });

    return { buckets, lastBucket };
  }

  /**
   * Map a viewed product to one affinity bucket.
   * Type first, then TCG disambiguation, then collections / vendor.
   * @param {object} product
   * @returns {string}
   */
  static classifyProduct(product) {
    if (!product) return '';

    const type = String(product.type || '').toLowerCase().trim();
    const vendor = String(product.vendor || '').toLowerCase().trim();
    const title = String(product.title || '').toLowerCase();
    const handle = String(product.handle || '').toLowerCase();
    const tags = (product.tags || []).map((t) => String(t).toLowerCase());
    const collections = (product.collections || []).map((c) => String(c).toLowerCase());
    const haystack = [type, vendor, title, handle, tags.join(' '), collections.join(' ')].join(' ');

    if (type === 'trading card accessories') return 'accessories';
    if (type === 'jewellery' || type === 'jewllery' || type === 'jewelry') return 'jewellery';
    if (vendor.includes('couture kingdom')) return 'jewellery';
    if (type === 'apparel') return 'apparel';
    if (type === 'ttrpg' || type === 'book' || type === 'dice') return 'tabletop';
    if (type === 'trading card single') return 'singles';
    if (type === 'art print' || type === 'artists' || type === 'local artists' || type === 'local artist') {
      return 'artists';
    }
    if (type === 'pocketpop' || type === 'replica' || type === 'plush' || type === 'fashion bag') {
      return 'collectables';
    }

    if (type === 'trading cards') {
      const tcg = this.#classifyTcg(haystack, tags, title, collections);
      if (tcg) return tcg;
    }

    const fromCollections = this.#classifyCollections(collections, haystack);
    if (fromCollections) return fromCollections;

    if (this.#isArtistVendor(vendor)) return 'artists';

    if (type === 'handbag' || type === 'wallet') return 'collectables';

    return '';
  }

  /**
   * @param {string} haystack
   * @param {string[]} tags
   * @param {string} title
   * @param {string[]} collections
   */
  static #classifyTcg(haystack, tags, title, collections) {
    const blob = `${tags.join(' ')} ${title} ${collections.join(' ')} ${haystack}`;
    if (this.#hasAny(blob, ['yu-gi-oh', 'yugioh', 'yu gi oh', 'yugi'])) return 'yugioh';
    if (this.#hasAny(blob, ['lorcana'])) return 'lorcana';
    if (this.#hasAny(blob, ['one piece', 'one-piece', 'onepiece'])) return 'onepiece';
    if (this.#hasAny(blob, ['pokémon', 'pokemon'])) return 'pokemon';
    if (this.#hasAny(blob, ['magic the gathering', 'magic: the gathering', ' mtg', 'mtg ', 'mtg-', '-mtg'])) {
      return 'mtg';
    }
    if (/\bmtg\b/.test(blob) || /\bmagic\b/.test(blob)) return 'mtg';
    return '';
  }

  /**
   * @param {string[]} collections
   * @param {string} haystack
   */
  static #classifyCollections(collections, haystack) {
    const handles = collections.join(' ');

    if (this.#handleMatch(collections, ['trading-card-accessories', 'acrylix-tcg-accessories', 'mamoru-tcg-accessories'])) {
      return 'accessories';
    }
    if (this.#handleMatch(collections, ['couture-kingdom', 'jewellery', 'jewellery-earrings', 'jewellery-necklaces', 'jewellery-bracelets'])) {
      return 'jewellery';
    }
    if (this.#handleMatch(collections, ['apparel', 'new-apparel', 'apparel-tees', 'apparel-hoodies', 'apparel-jackets', 'apparel-clothing', 'apparel-loungefly'])) {
      return 'apparel';
    }
    if (
      this.#handleMatch(collections, [
        'dungeons-dragons',
        'd-d-world-of-warcraft',
        'ttrpg-books',
        'ttrpg-paints',
        'board-games',
        'miniatures-and-paints',
      ]) ||
      collections.some((h) => h.startsWith('ttrpg-'))
    ) {
      return 'tabletop';
    }
    if (this.#handleMatch(collections, this.#ARTIST_HANDLES) || collections.some((h) => h.startsWith('artist-'))) {
      return 'artists';
    }
    if (this.#handleMatch(collections, ['collectables', 'action-figures', 'squishmallows'])) {
      return 'collectables';
    }
    if (this.#handleMatch(collections, ['tcg-chase-singles', 'all-trading-card-singles', 'tcg-singles-and-graded-cards', 'all-tcg'])) {
      return 'singles';
    }

    if (collections.some((h) => h.includes('lorcana')) || haystack.includes('lorcana')) return 'lorcana';
    if (collections.some((h) => h.includes('one-piece') || h.includes('onepiece'))) return 'onepiece';
    if (collections.some((h) => h.includes('yu-gi-oh') || h.includes('yugioh'))) return 'yugioh';
    if (collections.some((h) => h.includes('pokemon') || h.includes('pokémon'))) return 'pokemon';
    if (
      collections.some(
        (h) =>
          h.includes('mtg') ||
          h.includes('magic-the-gathering') ||
          h.startsWith('magic-') ||
          h.includes('aetherdrift-mtg') ||
          h.includes('final-fantasy-mtg')
      )
    ) {
      return 'mtg';
    }

    if (handles.includes('accessories')) return 'accessories';
    return '';
  }

  /**
   * @param {string} vendor
   */
  static #isArtistVendor(vendor) {
    if (!vendor) return false;
    if (this.#ARTIST_VENDORS.some((name) => vendor === name || vendor.includes(name))) return true;
    if (/^c-[a-z0-9]{2,5}$/.test(vendor)) return true;
    return false;
  }

  /**
   * @param {string[]} handles
   * @param {string[]} needles
   */
  static #handleMatch(handles, needles) {
    return handles.some((h) => needles.includes(h));
  }

  /**
   * @param {string} blob
   * @param {string[]} needles
   */
  static #hasAny(blob, needles) {
    return needles.some((n) => blob.includes(n));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  static clearBrowse() {
    localStorage.removeItem(this.#BROWSE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }

  /**
   * @returns {{products: object[], buckets: Record<string, number>, lastBucket: string}}
   */
  static getBrowse() {
    try {
      const raw = JSON.parse(localStorage.getItem(this.#BROWSE_KEY) || 'null');
      if (raw && typeof raw === 'object') {
        return {
          products: Array.isArray(raw.products) ? raw.products : [],
          buckets: raw.buckets && typeof raw.buckets === 'object' ? raw.buckets : {},
          lastBucket: String(raw.lastBucket || ''),
        };
      }
    } catch {
      // ignore corrupt storage
    }
    return { products: [], buckets: {}, lastBucket: '' };
  }

  /**
   * Homepage helper: winning bucket, or empty when the band should stay hidden.
   * @returns {string}
   */
  static homepageBucket() {
    const { lastBucket, products } = this.getBrowse();
    if (!lastBucket || !products.length) return '';
    if (this.#HIDE_BUCKETS.includes(lastBucket)) return '';
    return lastBucket;
  }
}
